import { createTheme } from '@mui/material/styles';

import colors from '../constants/colors';

declare module '@mui/material/styles' {
  interface BreakpointOverrides {
    xs: false;
    sm: false;
    md: false;
    lg: false;
    xl: false;
    mobile: true;
    tablet: true;
    desktop: true;
  }
}

const theme = createTheme({
  palette: {
    primary: {
      main: colors.csaaSpectrumBlue50,
    },
    text: {
      primary: colors.csaaBlack,
      secondary: colors.csaaDark,
    },
  },
  breakpoints: {
    values: {
      mobile: 0,
      tablet: 640,
      desktop: 1040,
    },
  },
  typography: {
    fontFamily: ['RT Raleway'].join(','),
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: { html: { WebkitFontSmoothing: 'auto' } },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          color: colors.csaaSpectrumBlue50,
          '&:focus': {
            color: colors.csaaInteractiveBlue,
            outline: `solid 2px ${colors.csaaInteractiveBlue}`,
            borderRadius: 4,
            outlineOffset: '1px',
          },
          '&:hover': {
            color: colors.blueInteractiveButtonHover,
            textDecoration: 'underline',
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          '&.Mui-focusVisible': {
            borderTopLeftRadius: 8,
            borderTopRightRadius: 8,
            outline: `solid 2px ${colors.csaaBlack}`,
            outlineOffset: -2,
          },
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableRipple: true,
        disableFocusRipple: true,
      },
      styleOverrides: {
        root: {
          '&.Mui-disabled': {
            backgroundColor: colors.csaaNeutralBorder,
            color: colors.csaaMid,
          },
        },
      },
      variants: [
        {
          props: { variant: 'text' },
          style: {
            '&:focus': {
              color: colors.csaaInteractiveBlue,
              outline: `solid 2px ${colors.csaaInteractiveBlue}`,
              outlineOffset: '1px',
            },
            '&:hover': {
              backgroundColor: colors.blackInteractiveButtonHover,
            },
          },
        },
        {
          props: { variant: 'outlined' },
          style: {
            color: colors.csaaBlack,
            border: `solid 2px ${colors.csaaBlack}`,
            '&:focus': {
              border: `1px solid ${colors.csaaBlack}`,
              backgroundColor: colors.csaaOverlayBlack,
              outline: `solid 2px ${colors.csaaBlack}`,
              outlineOffset: '1px',
            },
            '&:hover': {
              backgroundColor: colors.blackInteractiveButtonHover,
            },
          },
        },
        {
          props: { variant: 'contained' },
          style: {
            color: colors.csaaWhite,
            backgroundColor: colors.csaaSpectrumBlue50,
            '&:focus': {
              border: `1px solid ${colors.csaaInteractiveBlue}`,
              backgroundColor: colors.csaaInteractiveBlue,
              outline: `solid 2px ${colors.csaaInteractiveBlue}`,
              outlineOffset: '1px',
            },
            '&:hover': {
              backgroundColor: colors.blueInteractiveButtonHover,
            },
          },
        },
      ],
    },
    MuiListItemButton: {
      defaultProps: {
        disableRipple: true,
      },
      styleOverrides: {
        root: {
          '&:focus': {
            outline: `solid 2px ${colors.csaaInteractiveBlue}`,
            outlineOffset: '1px',
            borderRadius: 4,
            backgroundColor: 'inherit',
          },
        },
      },
    },
    MuiIconButton: {
      defaultProps: {
        disableRipple: true,
      },
      styleOverrides: {
        root: {
          '&:focus': {
            outline: `solid 2px ${colors.csaaBlack}`,
            borderRadius: '50%',
            backgroundColor: 'inherit',
          },
          '&:hover': {
            backgroundColor: `${colors.csaaNeutral}`,
          },
        },
      },
    },
    MuiButtonBase: {
      defaultProps: {
        disableRipple: true,
      },
    },
    MuiButtonGroup: {
      defaultProps: {
        disableRipple: true,
        disableFocusRipple: true,
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          '.MuiSwitch-switchBase.Mui-checked:hover': {
            backgroundColor: colors.csaaSpectrumBlue50Hover,
          },
          '.MuiSwitch-switchBase:hover': {
            backgroundColor: colors.blackInteractiveButtonHover,
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          ...(ownerState.error !== true && {
            '& .MuiFormLabel-root.MuiInputLabel-root.Mui-focused': {
              color: colors.csaaBlack,
            },
            '& .MuiInputBase-root.Mui-focused .MuiOutlinedInput-notchedOutline':
              {
                borderColor: colors.csaaBlack,
              },
          }),
        }),
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          '&.Mui-focusVisible': {
            border: `solid 2px ${colors.csaaBlack}`,
          },
          '&.Mui-focusVisible.Mui-selected': {
            border: `solid 2px ${colors.csaaInteractiveBlue}`,
          },
        },
      },
    },
    MuiUseMediaQuery: {
      defaultProps: {
        noSsr: true,
      },
    },
  },
});

export default theme;
